import React, { useContext } from "react"
import { IntlConfig, IntlProvider, useIntl } from "react-intl"
import { Helmet } from "react-helmet"
import { AdapterDayjs, LocalizationProvider, dayjs } from "@carrotfertility/carotene-core"
import { setHeapLocale } from "utils/heap"
import { getLastUsedLocale, setLastUsedLocale } from "../../../utils/lastUsedLocale"
import { setOneTrustLocale } from "utils/oneTrustLocale"
import { DEFAULT_LOCALE, SupportedLocale } from "../../../types/Locale"
import { reportError } from "utils/ErrorReporting"

type CompiledMessages = IntlConfig["messages"]

type LocaleProps = {
  setLocale: (locale: SupportedLocale) => void
  didCompiledMessagesLoad: boolean
}

const LocaleContext = React.createContext<LocaleProps>({
  setLocale: null,
  didCompiledMessagesLoad: false
})

function loadLocaleMessages(locale: SupportedLocale): Promise<CompiledMessages> {
  return import(`compiledLang/${locale.toString()}.json`)
}

export const useLocaleContext = () => useContext(LocaleContext)

export function IntlProviderWrapper(props: React.PropsWithChildren) {
  const lastUsedLocale = getLastUsedLocale()

  const [locale, setLocale] = React.useState<SupportedLocale>(lastUsedLocale ?? DEFAULT_LOCALE)
  const [compiledMessages, setCompiledMessages] = React.useState<CompiledMessages>()
  const [didCompiledMessagesLoad, setDidCompiledMessagesLoad] = React.useState<boolean>(false)

  React.useEffect(() => {
    setOneTrustLocale(locale)
    setLastUsedLocale(locale)
    setHeapLocale(locale)
    async function getCompiledMessages(locale: SupportedLocale): Promise<void> {
      try {
        const messages = await loadLocaleMessages(locale)
        setCompiledMessages(messages)
        setDidCompiledMessagesLoad(true)
      } catch (err) {
        reportError(
          new Error(`Could not load locale messages\nLocale: [${locale?.toString()}] \nCause:${err.message}`, {
            cause: err.cause
          })
        )
      }
    }
    getCompiledMessages(locale)
  }, [locale])

  return (
    <LocaleContext.Provider value={{ setLocale, didCompiledMessagesLoad }}>
      <IntlProvider locale={locale?.toString()} defaultLocale={DEFAULT_LOCALE.toString()} messages={compiledMessages}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjs.locale(locale?.toString())}>
          <HtmlLang />
          {props.children}
        </LocalizationProvider>
      </IntlProvider>
    </LocaleContext.Provider>
  )
}

function HtmlLang() {
  const { locale } = useIntl()

  return (
    <Helmet>
      <html lang={locale} />
    </Helmet>
  )
}
